import { Cesium } from 'app/cesium';
import { PanesService } from 'app/main/PanesService';
import { IRootScopeService } from 'angular';
import { EventRegistry } from 'app/shared/events/event_registry';
import { TimeZoneManager } from 'app/shared/time/TimeZoneManager';
import { formatDate } from 'app/shared/utilities/time';
import { Format } from 'app/shared/utilities/TimestampFormatter';

const TIMELINE_SCRUB_MODE = 1;

interface HackedCesiumTimeline extends Cesium.Timeline {
    _makeTics: Function;
    _onMouseWheel: any;
    _lastXPos: number;
    _timeBarEle: HTMLElement;
    addEventListener: any;
    makeLabel: any;
    _mouseMode: number;
}

export class Timeline {
    private readonly _$rootScope: IRootScopeService;
    private readonly _panesService: PanesService;
    private readonly _cesiumTimeline: HackedCesiumTimeline;
    private readonly _timelineSetEvent;
    private _timeBarSecondsSpan: number;
    private _timeZoneString: string;

    /**
     * A simple abstraction of the Cesium Timeline.
     * Use with a clockHandler -> new clockHandler(new Timeline(new Cesium.Timeline()))
     * @param cesiumTimeline
     * @param panesService
     * @param $rootScope
     * @param timeZoneManager
     */
    constructor(
        cesiumTimeline: HackedCesiumTimeline,
        panesService: PanesService,
        $rootScope: IRootScopeService,
        timeZoneManager: TimeZoneManager,
    ) {
        this._$rootScope = $rootScope;

        this._panesService = panesService;

        // monkey patch the makeTics function
        cesiumTimeline._makeTics = imsMakeTics;

        this._cesiumTimeline = cesiumTimeline;
        this._timelineSetEvent = new EventRegistry();

        // Remove timeline mousewheel listeners
        this._cesiumTimeline._timeBarEle.removeEventListener(
            'mousewheel',
            cesiumTimeline._onMouseWheel,
        );
        this._cesiumTimeline._timeBarEle.removeEventListener(
            'DOMMouseScroll',
            cesiumTimeline._onMouseWheel,
        );
        // this._cesiumTimeline._timeBarEle.removeEventListener('mousedown', cesiumTimeline._onMouseDown);
        // this._cesiumTimeline._timeBarEle.removeEventListener('touchstart', cesiumTimeline._onTouchStart);
        // this._cesiumTimeline._timeBarEle.removeEventListener('touchmove', cesiumTimeline._onTouchMove);
        // this._cesiumTimeline._timeBarEle.removeEventListener('touchend', cesiumTimeline._onTouchEnd);
        // this._cesiumTimeline._timeBarEle.removeEventListener('touchcancel', cesiumTimeline._onTouchEnd);

        // this is important! Without it, you get some janky looking scrubbing when the cursor reaches the edge
        this._cesiumTimeline.addEventListener('settime', this._timelineTimeSet.bind(this), false);

        timeZoneManager.onChange((tz) => {
            // cache the new timezone string
            this._timeZoneString = tz.string;
            // we want to force an update to the timeline so it redraws the times in the new time zone
            this._cesiumTimeline._makeTics();
        });

        this._cesiumTimeline.makeLabel = (time: Cesium.JulianDate) => {
            return formatDate(JulianDate.toDate(time), this._timeZoneString, Format.JUST_TIME);
        };
    }

    _timelineTimeSet() {
        this._timelineSetEvent.notifyListeners();
    }

    isScrubbing() {
        // Be on the look out for a better way to know if the user is scrubbing the timeline cursor!
        return this._cesiumTimeline._mouseMode === TIMELINE_SCRUB_MODE;
    }

    lookAt(startJulian, stopJulian) {
        // HACK: Make sure the scrub element i.e. the time cursor updates.
        // set the last x pos to something impossible to force an update
        // See Timeline.prototype.updateFromClock in the cesium Timeline.js file
        // This is to fix the problem where the cursor would not update when picking a new
        // date time. Because the lastXPos was not changing the cursor updating code inside cesium never
        // ran
        this._cesiumTimeline._lastXPos = -1;
        // If the user ISN'T dragging to timeline to causing it to scrub
        if (!this.isScrubbing()) {
            // Push the timeline, otherwise CESIUM is going to handle scrubbing the timeline zooming
            this._cesiumTimeline.zoomTo(startJulian, stopJulian);
        }
        // otherwise the timeline is being scrubbed - don't override the user's input and force zoomTo
    }

    /**
     * This event is triggered when the user finishes a time scrub
     * @param timeSetListener
     * @returns {function()}
     */
    addSetTimeListener(timeSetListener) {
        return this._timelineSetEvent.addEventListener(timeSetListener);
    }
}

// Constants required for monkey patching the makeTics function in the cesium timeline
let JulianDate = Cesium.JulianDate;
let timelineTicScales = [
    0.001,
    0.002,
    0.005,
    0.01,
    0.02,
    0.05,
    0.1,
    0.25,
    0.5,
    1.0,
    2.0,
    5.0,
    10.0,
    15.0,
    30.0,
    60.0, // 1min
    120.0, // 2min
    300.0, // 5min
    600.0, // 10min
    900.0, // 15min
    1800.0, // 30min
    3600.0, // 1hr
    7200.0, // 2hr
    14400.0, // 4hr
    21600.0, // 6hr
    43200.0, // 12hr
    86400.0, // 24hr
    172800.0, // 2days
    345600.0, // 4days
    604800.0, // 7days
    1296000.0, // 15days
    2592000.0, // 30days
    5184000.0, // 60days
    7776000.0, // 90days
    15552000.0, // 180days
    31536000.0, // 365days
    63072000.0, // 2years
    126144000.0, // 4years
    157680000.0, // 5years
    315360000.0, // 10years
    630720000.0, // 20years
    1261440000.0, // 40years
    1576800000.0, // 50years
    3153600000.0, // 100years
    6307200000.0, // 200years
    12614400000.0, // 400years
    15768000000.0, // 500years
    31536000000.0, // 1000years
];

/**
 * Intended to be monkey patched onto a cesium timeline to improve drawing performance.
 * Changes:
 * 1. We don't draw the tiny tics, for the most part they are covered up by the loading bars.
 * 2. Fixed a few unnecessary reflows.
 */
function imsMakeTics() {
    let timeBar = this._timeBarEle;

    if (!this._scrubJulian || !this._startJulian || !this._timeBarSecondsSpan || !this._topDiv) {
        // we're not ready yet, return
        return;
    }

    let seconds = JulianDate.secondsDifference(this._scrubJulian, this._startJulian);
    let xPos = Math.round((seconds * this._topDiv.clientWidth) / this._timeBarSecondsSpan);
    let scrubX = xPos - 8,
        tic;
    let widget = this;

    this._needleEle.style.left = xPos.toString() + 'px';

    let tics = '';

    let minimumDuration = 0.01;
    let maximumDuration = 31536000000.0; // ~1000 years
    let epsilon = 1e-10;

    // If time step size is known, enter it here...
    let minSize = 0;

    let duration = this._timeBarSecondsSpan;
    if (duration < minimumDuration) {
        duration = minimumDuration;
        this._timeBarSecondsSpan = minimumDuration;
        this._endJulian = JulianDate.addSeconds(
            this._startJulian,
            minimumDuration,
            new JulianDate(),
        );
    } else if (duration > maximumDuration) {
        duration = maximumDuration;
        this._timeBarSecondsSpan = maximumDuration;
        this._endJulian = JulianDate.addSeconds(
            this._startJulian,
            maximumDuration,
            new JulianDate(),
        );
    }

    let timeBarWidth = this._timeBarEle.clientWidth;
    if (timeBarWidth < 10) {
        timeBarWidth = 10;
    }
    let startJulian = this._startJulian;

    // epsilonTime: a small fraction of one pixel width of the timeline, measured in seconds.
    let epsilonTime = Math.min((duration / timeBarWidth) * 1e-5, 0.4);

    // epochJulian: a nearby time to be considered "zero seconds", should be a round-ish number by human standards.
    let epochJulian;
    if (duration > 315360000) {
        // 3650+ days visible, epoch is start of the first visible century.
        epochJulian = JulianDate.fromIso8601(
            JulianDate.toDate(startJulian).toISOString().substring(0, 2) + '00-01-01T00:00:00Z',
        );
    } else if (duration > 31536000) {
        // 365+ days visible, epoch is start of the first visible decade.
        epochJulian = JulianDate.fromIso8601(
            JulianDate.toDate(startJulian).toISOString().substring(0, 3) + '0-01-01T00:00:00Z',
        );
    } else if (duration > 86400) {
        // 1+ day(s) visible, epoch is start of the year.
        epochJulian = JulianDate.fromIso8601(
            JulianDate.toDate(startJulian).toISOString().substring(0, 4) + '-01-01T00:00:00Z',
        );
    } else {
        // Less than a day on timeline, epoch is midnight of the visible day.
        epochJulian = JulianDate.fromIso8601(
            JulianDate.toDate(startJulian).toISOString().substring(0, 10) + 'T00:00:00Z',
        );
    }
    // startTime: Seconds offset of the left side of the timeline from epochJulian.
    let startTime = JulianDate.secondsDifference(
        this._startJulian,
        JulianDate.addSeconds(epochJulian, epsilonTime, new JulianDate()),
    );
    // endTime: Seconds offset of the right side of the timeline from epochJulian.
    let endTime = startTime + duration;
    this._epochJulian = epochJulian;

    function getStartTic(ticScale) {
        return Math.floor(startTime / ticScale) * ticScale;
    }

    function getNextTic(tic, ticScale) {
        return Math.ceil(tic / ticScale + 0.5) * ticScale;
    }

    function getAlpha(time) {
        return (time - startTime) / duration;
    }

    function remainder(x, y) {
        //return x % y;
        return x - y * Math.round(x / y);
    }

    // Width in pixels of a typical label, plus padding
    this._rulerEle.innerHTML = this.makeLabel(
        JulianDate.addSeconds(this._endJulian, -minimumDuration, new JulianDate()),
    );
    let sampleWidth = this._rulerEle.offsetWidth + 20;
    if (sampleWidth < 30) {
        // Workaround an apparent IE bug with measuring the width after going full-screen from inside an iframe.
        sampleWidth = 180;
    }

    let origMinSize = minSize;
    minSize -= epsilon;

    let renderState: any = {
        startTime: startTime,
        startJulian: startJulian,
        epochJulian: epochJulian,
        duration: duration,
        timeBarWidth: timeBarWidth,
        getAlpha: getAlpha,
    };
    this._highlightRanges.forEach(function (highlightRange) {
        tics += highlightRange.render(renderState);
    });

    // Calculate tic mark label spacing in the TimeBar.
    let mainTic = 0.0,
        subTic = 0.0,
        tinyTic = 0.0;
    // Ideal labeled tic as percentage of zoom interval
    let idealTic = sampleWidth / timeBarWidth;
    if (idealTic > 1.0) {
        // Clamp to width of window, for thin windows.
        idealTic = 1.0;
    }
    // Ideal labeled tic size in seconds
    idealTic *= this._timeBarSecondsSpan;
    let ticIndex = -1,
        smallestIndex = -1;

    let i,
        ticScaleLen = timelineTicScales.length;
    for (i = 0; i < ticScaleLen; ++i) {
        let sc = timelineTicScales[i];
        ++ticIndex;
        mainTic = sc;
        // Find acceptable main tic size not smaller than ideal size.
        if (sc > idealTic && sc > minSize) {
            break;
        }
        if (
            smallestIndex < 0 &&
            timeBarWidth * (sc / this._timeBarSecondsSpan) >= this.smallestTicInPixels
        ) {
            smallestIndex = ticIndex;
        }
    }
    if (ticIndex > 0) {
        while (
            ticIndex > 0 // Compute sub-tic size that evenly divides main tic.
        ) {
            --ticIndex;
            if (Math.abs(remainder(mainTic, timelineTicScales[ticIndex])) < 0.00001) {
                if (timelineTicScales[ticIndex] >= minSize) {
                    subTic = timelineTicScales[ticIndex];
                }
                break;
            }
        }

        if (smallestIndex >= 0) {
            while (
                smallestIndex < ticIndex // Compute tiny tic size that evenly divides sub-tic.
            ) {
                if (
                    Math.abs(remainder(subTic, timelineTicScales[smallestIndex])) < 0.00001 &&
                    timelineTicScales[smallestIndex] >= minSize
                ) {
                    tinyTic = timelineTicScales[smallestIndex];
                    break;
                }
                ++smallestIndex;
            }
        }
    }

    minSize = origMinSize;
    if (minSize > epsilon && tinyTic < 0.00001 && Math.abs(minSize - mainTic) > epsilon) {
        tinyTic = minSize;
        if (minSize <= mainTic + epsilon) {
            subTic = 0.0;
        }
    }

    let lastTextLeft = -999999;
    // We no longer draw tiny tics!
    // if ((timeBarWidth * (tinyTic / this._timeBarSecondsSpan)) >= 3.0) {
    //     for (tic = getStartTic(tinyTic); tic <= endTime; tic = getNextTic(tic, tinyTic)) {
    //         tics += '<span class="cesium-timeline-ticTiny" style="left: ' + Math.round(timeBarWidth * getAlpha(tic)).toString() + 'px;"></span>';
    //     }
    // }
    if (timeBarWidth * (subTic / this._timeBarSecondsSpan) >= 3.0) {
        for (tic = getStartTic(subTic); tic <= endTime; tic = getNextTic(tic, subTic)) {
            tics +=
                '<span class="cesium-timeline-ticSub" style="left: ' +
                Math.round(timeBarWidth * getAlpha(tic)).toString() +
                'px;"></span>';
        }
    }
    let textWidth = this._rulerEle.offsetWidth;
    //let textWidth;
    if (timeBarWidth * (mainTic / this._timeBarSecondsSpan) >= 2.0) {
        this._mainTicSpan = mainTic;
        endTime += mainTic;
        tic = getStartTic(mainTic);
        let leapSecond = JulianDate.computeTaiMinusUtc(epochJulian);
        while (tic <= endTime) {
            let ticTime = JulianDate.addSeconds(startJulian, tic - startTime, new JulianDate());
            if (mainTic > 2.1) {
                let ticLeap = JulianDate.computeTaiMinusUtc(ticTime);
                if (Math.abs(ticLeap - leapSecond) > 0.1) {
                    tic += ticLeap - leapSecond;
                    ticTime = JulianDate.addSeconds(startJulian, tic - startTime, new JulianDate());
                }
            }
            let ticLeft = Math.round(timeBarWidth * getAlpha(tic));
            let ticLabel = this.makeLabel(ticTime);
            this._rulerEle.innerHTML = ticLabel;
            //textWidth = this._rulerEle.offsetWidth;

            if (textWidth < 10) {
                // IE iframe fullscreen sampleWidth workaround, continued.
                textWidth = sampleWidth;
            }
            let labelLeft = ticLeft - (textWidth / 2 - 1);
            if (labelLeft > lastTextLeft) {
                lastTextLeft = labelLeft + textWidth + 5;
                tics +=
                    '<span class="cesium-timeline-ticMain" style="left: ' +
                    ticLeft.toString() +
                    'px;"></span>' +
                    '<span class="cesium-timeline-ticLabel" style="left: ' +
                    labelLeft.toString() +
                    'px;">' +
                    ticLabel +
                    '</span>';
            } else {
                tics +=
                    '<span class="cesium-timeline-ticSub" style="left: ' +
                    ticLeft.toString() +
                    'px;"></span>';
            }
            tic = getNextTic(tic, mainTic);
        }
    } else {
        this._mainTicSpan = -1;
    }

    tics +=
        '<span class="cesium-timeline-icon16" style="left:' +
        scrubX +
        'px;bottom:0;background-position: 0 0;"></span>';
    timeBar.innerHTML = tics;
    this._scrubElement = timeBar.lastChild;

    // Clear track canvas.
    this._context.clearRect(0, 0, this._trackListEle.width, this._trackListEle.height);

    renderState.y = 0;
    this._trackList.forEach(function (track) {
        track.render(widget._context, renderState);
        renderState.y += track.height;
    });
}
